import React, { type ReactNode } from "react"

import { LoaderCircle } from "lucide-react"

import { cn } from "@/lib/utils"

export function CardHeader({ children }: { children: ReactNode }) {
  return children
}

export function CardFooter({ children }: { children: ReactNode }) {
  return children
}

const extractMainCardChildren = (
  children: ReactNode,
): {
  title: ReactNode | undefined
  footer: ReactNode | undefined
  body: ReactNode[]
} => {
  let title: ReactNode | undefined = undefined
  let footer: ReactNode | undefined = undefined
  const body: ReactNode[] = []

  React.Children.toArray(children).forEach((child) => {
    if (React.isValidElement(child)) {
      if (child.type === CardHeader) {
        title = child
      } else if (child.type === CardFooter) {
        footer = child
      } else {
        body.push(child)
      }
    } else if (typeof child === "string") {
      body.push(child)
    }
  })
  return { title, body, footer }
}
type MainCardProps = {
  children: ReactNode
  className?: string
}

function FallbackSpinner() {
  return (
    <div className="grid h-full place-items-center">
      <LoaderCircle className="text-primary size-12 animate-spin" />
    </div>
  )
}

export function MainCard({ children, className }: MainCardProps) {
  const { title, body, footer } = extractMainCardChildren(children)
  return (
    <div
      className={cn(
        "flex h-full flex-col justify-between gap-8 rounded-2xl bg-white p-8 shadow-sm",
        className,
      )}
    >
      <h2 className="border-primary border-l-4 pl-3 text-xl font-semibold">
        {title}
      </h2>
      <div className="flex h-full flex-col space-y-6">
        <React.Suspense fallback={<FallbackSpinner />}>{body}</React.Suspense>
      </div>
      <div className="mt-auto flex gap-4 self-end">{footer}</div>
    </div>
  )
}

MainCard.Header = CardHeader
MainCard.Footer = CardFooter
